import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import { USER_ROLE_LABELS } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import { eventAPI } from '../../api/endpoints';

const AddUserModal = ({ isOpen, onClose, onSubmit, loading }) => {
  const { permissions } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'usher',
    event_id: '',
  });
  const [errors, setErrors] = useState({});

  // Get allowed roles for current user
  const allowedRoles = permissions.getAllowedRolesToCreate();

  // Fetch events for event admins
  const { data: eventsData } = useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const response = await eventAPI.getAll();
      return response.data;
    },
    enabled: permissions.isEventAdmin() && isOpen,
  });

  const events = eventsData?.events || [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.email.includes('@')) newErrors.email = 'Invalid email';
    if (!formData.full_name) newErrors.full_name = 'Full name is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    
    // Event admin must select an event
    if (permissions.isEventAdmin() && !formData.event_id) {
      newErrors.event_id = 'Please select an event';
    }
    
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    console.log('🚀 AddUserModal - Submitting user data:', formData);
    console.log('🚀 AddUserModal - Allowed roles:', allowedRoles);
    console.log('🚀 AddUserModal - Is event admin:', permissions.isEventAdmin());
    
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({ email: '', full_name: '', password: '', role: 'usher', event_id: '' });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-xl font-semibold text-gray-900">Add New User</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <Input
              label="Full Name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              error={errors.full_name}
              placeholder="John Doe"
              required
            />

            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              placeholder="john@example.com"
              required
            />

            <Input
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              placeholder="Minimum 6 characters"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {allowedRoles.map(role => (
                  <option key={role} value={role}>
                    {USER_ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role}</p>
              )}
            </div>

            {/* Event Selection for Event Admins */}
            {permissions.isEventAdmin() && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign to Event
                </label>
                <select
                  name="event_id"
                  value={formData.event_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select an event...</option>
                  {events.map(event => (
                    <option key={event.id} value={event.id}>
                      {event.name}
                    </option>
                  ))}
                </select>
                {errors.event_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.event_id}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  User will be automatically assigned to the selected event
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                loading={loading}
              >
                Add User
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
